import {
  createSelectorHooks,
  type ZustandHookSelectors,
} from 'auto-zustand-selectors-hook';
import { type z } from 'zod/v4-mini';
import { create } from 'zustand';

import { i18n } from '$/components/core/Localization/18n';
import { logError, trackEvent } from '$/logger';
import { type FavoriteItem } from '$/pages/DashboardPages/pages/Favorite';
import {
  addFavorite,
  addFolder,
  changeFoldersOfFavorite,
  duplicateFolder,
  getAllFavoritesAndFolders,
  removeFavorite,
  removeFolder,
  renameFolder,
} from '$/services/usecases/favorites';
import {
  type FavoriteFolderSchema,
  type FavoriteSchema,
} from '$/services/usecases/favorites/mapper/getFavoritesResponse';
import { type MaterialType } from '$/services/usecases/materials/mapper/materials';
import { mapMaterialTypeToCategory } from '$/utils/piwikUtils';

export const DEFAULTFOLDERNAME = 'default';

export type Favorite = z.infer<typeof FavoriteSchema>;

export type FavoriteFolder = z.infer<typeof FavoriteFolderSchema>;

export type FavoriteSectionGroup = {
  id: string;
  title?: string;
  materialType: MaterialType;
  favorites: FavoriteItem[];
};

type State = {
  favorites: Favorite[];
  favoriteFolders: FavoriteFolder[];
  globalFavoriteFolders: FavoriteFolder[];
  activeFolderId: string | null;
  favoritesInitiated: boolean;
  folderSectionGroups: Partial<Record<MaterialType, FavoriteSectionGroup[]>>;
};

type Actions = {
  getFavorites: (skipCheck?: boolean) => Promise<{
    favorites: Favorite[];
    favoriteFolders: FavoriteFolder[];
  }>;
  addFavorite: (
    materialId: Favorite['materialId'],
    materialType?: MaterialType,
    materialLabel?: string,
    parentFolders?: Favorite['parentFolderIds'],
  ) => Promise<void>;
  removeFavorite: (favoriteId: Favorite['materialId']) => Promise<void>;
  toogleFavorite: (
    favoriteId: Favorite['materialId'],
    favoriteType?: MaterialType,
    favoriteLabel?: string,
  ) => Promise<void>;
  changeFoldersOfFavorite: (
    favoriteId: Favorite['materialId'],
    folders: Favorite['parentFolderIds'],
  ) => Promise<void>;
  removeFavoriteFromFolder: (
    favoriteId: Favorite['materialId'],
    folderId: Favorite['parentFolderIds'][number],
  ) => Promise<void>;
  addFavoriteToFolder: (
    favoriteId: Favorite['materialId'],
    folderId: Favorite['parentFolderIds'][number],
  ) => Promise<void>;
  isFavorite: (materialId: Favorite['materialId']) => boolean;
  addFolder: (
    name?: FavoriteFolder['name'],
  ) => Promise<FavoriteFolder['id'] | null>;
  removeFolder: (id: FavoriteFolder['id']) => Promise<void>;
  duplicateFolder: (
    id: FavoriteFolder['id'],
    nameSuffix: string,
  ) => Promise<void>;
  renameFolder: (
    id: FavoriteFolder['id'],
    newName: FavoriteFolder['name'],
  ) => Promise<void>;
  setActiveFolderId: (id: FavoriteFolder['id'] | null) => void;
};

const initialValues: State = {
  favorites: [],
  favoriteFolders: [],
  globalFavoriteFolders: [],
  activeFolderId: null,
  favoritesInitiated: false,
  folderSectionGroups: {},
};

const store = create<State & Actions>()((set, get) => ({
  ...initialValues,
  getFavorites: async (skipCheck = false) => {
    if (!skipCheck && get().favoritesInitiated)
      return {
        favorites: get().favorites,
        favoriteFolders: get().favoriteFolders,
      };

    const response = await getAllFavoritesAndFolders();

    if (response.isSuccessful) {
      set({
        favorites: response.response.payload?.favoriteMaterials ?? [],
        favoriteFolders: response.response.payload?.favoriteFolders ?? [],
        globalFavoriteFolders:
          response.response.payload?.globalFavoriteFolders ?? [],
        favoritesInitiated: true,
      });
    }
    return {
      favorites: get().favorites,
      favoriteFolders: get().favoriteFolders,
      globalFavoriteFolders: get().globalFavoriteFolders,
    };
  },
  addFavorite: async (
    materialId,
    materialType,
    materialLabel,
    parentFolders = [DEFAULTFOLDERNAME],
  ) => {
    if (materialId && materialType && materialLabel) {
      trackEvent(
        mapMaterialTypeToCategory(materialType),
        'AddFavorite',
        materialLabel,
      );
    } else {
      logError({
        message: 'Could not track Add Favorite event. Missing data.',
        data: {
          materialId,
          materialType,
          materialLabel,
        },
      });
    }

    const response = await addFavorite(materialId, parentFolders);

    if (response.isSuccessful) {
      set((state) => ({
        favorites: [
          ...state.favorites,
          {
            materialId,
            parentFolderIds: [DEFAULTFOLDERNAME],
            isGlobalFavoriteMaterial: false,
          },
        ],
      }));
    }
  },
  removeFavorite: async (materialId) => {
    const response = await removeFavorite(materialId);

    if (response.isSuccessful) {
      set(({ favorites }) => ({
        favorites: favorites.filter(
          (favorite) => favorite.materialId !== materialId,
        ),
      }));
      await get().getFavorites(true);
    }
  },
  toogleFavorite: async (materialId, materialType, materialLabel) => {
    if (get().isFavorite(materialId)) {
      await get().removeFavorite(materialId);
    } else {
      await get().addFavorite(materialId, materialType, materialLabel);
    }
  },
  isFavorite: (materialId) => {
    return (
      get().favorites.find(
        (favorite) =>
          favorite.materialId === materialId &&
          favorite.parentFolderIds.length > 0 &&
          !favorite.isGlobalFavoriteMaterial,
      ) != null
    );
  },
  changeFoldersOfFavorite: async (materialId, folderIds) => {
    const previousFavorites = get().favorites;
    set(({ favorites }) => ({
      favorites: favorites.map((favorite) =>
        favorite.materialId === materialId
          ? { ...favorite, parentFolderIds: folderIds }
          : favorite,
      ),
    }));

    const globalFolderIds = get().globalFavoriteFolders.map(
      (folder) => folder.id,
    );
    const response = await changeFoldersOfFavorite(
      materialId,
      folderIds.filter((folderId) => !globalFolderIds.includes(folderId)),
    );

    if (!response.isSuccessful) {
      set({ favorites: previousFavorites });
    } else {
      await get().getFavorites(true);
    }
  },
  removeFavoriteFromFolder: async (favoriteId, folderId) => {
    const favorite = get().favorites.find(
      (favorite) => favorite.materialId === favoriteId,
    );

    if (favorite == null) return;

    await get().changeFoldersOfFavorite(
      favoriteId,
      favorite?.parentFolderIds.filter((folder) => folder !== folderId),
    );
  },
  addFavoriteToFolder: async (favoriteId, folderId) => {
    const favorite = get().favorites.find(
      (favorite) => favorite.materialId === favoriteId,
    );

    if (favorite == null) return;

    await get().changeFoldersOfFavorite(favoriteId, [
      ...favorite.parentFolderIds,
      folderId,
    ]);
  },
  addFolder: async (name) => {
    trackEvent('Favorites', 'NewFolder');

    const folderName =
      name ??
      i18n.t('favorites.defaultFolderName', {
        letter: get().favoriteFolders.length + 1,
      });

    const response = await addFolder(folderName);

    if (response.isSuccessful && response.response.payload) {
      set({
        favoriteFolders: [...get().favoriteFolders, response.response.payload],
      });
      return response.response.payload.id;
    }

    return null;
  },
  removeFolder: async (id: string) => {
    const response = await removeFolder(id);

    if (response.isSuccessful) {
      await get().getFavorites(true);

      if (get().activeFolderId === id) {
        set({ activeFolderId: null });
      }
    }
  },
  renameFolder: async (id: string, newName: string) => {
    const response = await renameFolder(id, newName);

    if (response.isSuccessful) {
      const folders = [...get().favoriteFolders];
      const folderIndex = folders.findIndex((folder) => folder.id === id);
      folders[folderIndex] = {
        id,
        name: newName,
        isGlobalFavoriteFolder: false,
        hasThumbnail: false,
        materialOrder: [],
      };
      set({ favoriteFolders: folders });
    }
  },
  duplicateFolder: async (id, nameSuffix) => {
    await duplicateFolder(id, nameSuffix);
    await get().getFavorites(true);
  },
  setActiveFolderId: (id: string | null) => {
    set({ activeFolderId: id });
  },
}));

export const useFavoriteStore = createSelectorHooks(store) as typeof store &
  ZustandHookSelectors<State & Actions>;
